/**
 * Phase 88 — Liste des tournées de réapprovisionnement.
 * Route : /tournees-reappro
 *
 * Permet à l'opérateur de :
 *  1. Voir l'historique des tournées (BROUILLON / EN_PREPA / CLOTUREE)
 *  2. Lancer une nouvelle tournée en sélectionnant les distributeurs visités
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Truck, Loader2, MapPin, FileText } from "lucide-react";
import { toast } from "sonner";
import { pdf } from "@react-pdf/renderer";
import ReapproManualPdf from "@/components/ReapproManualPdf";

const STATUS_META = {
  BROUILLON: { label: "Brouillon", cls: "bg-[#F4F4F0] text-[#575A57] border-[#E5E5E0]" },
  EN_PREPA:  { label: "En préparation", cls: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8]" },
  CLOTUREE:  { label: "Clôturée", cls: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
};

export default function RefillTours() {
  const navigate = useNavigate();
  const [tours, setTours] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [distributors, setDistributors] = useState([]);
  const [selectedIds, setSelectedIds] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/refill-tours");
      setTours(data.tours || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement tournées");
      setTours([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = async () => {
    try {
      const { data } = await api.get("/distributors");
      // Filter out CLIENT distributors and the central depot itself
      const list = (Array.isArray(data) ? data : data?.distributors || []).filter(
        (d) => d.type === "AUTOMATIQUE"
      );
      setDistributors(list);
      setSelectedIds({});
      setDialogOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement distributeurs");
    }
  };

  const toggleAll = (checked) => {
    const newSel = {};
    if (checked) distributors.forEach((d) => { newSel[d.id] = true; });
    setSelectedIds(newSel);
  };

  const submitCreate = async () => {
    const ids = Object.keys(selectedIds).filter((id) => selectedIds[id]);
    if (ids.length === 0) {
      toast.error("Sélectionnez au moins 1 distributeur");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/refill-tours", { selected_distributor_ids: ids });
      toast.success(`Tournée ${data.reference} créée — ${ids.length} distributeur(s)`);
      setDialogOpen(false);
      navigate(`/tournees-reappro/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création tournée");
    } finally {
      setCreating(false);
    }
  };

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="refill-tours-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1C1A] flex items-center gap-2">
            <Truck className="w-6 h-6 text-[#1F4E3D]" />
            Tournées de réapprovisionnement
          </h1>
          <p className="text-sm text-[#575A57] mt-1">
            Préparez le réappro de plusieurs distributeurs en un seul formulaire.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              const blob = await pdf(<ReapproManualPdf />).toBlob();
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank");
              setTimeout(() => URL.revokeObjectURL(url), 30000);
            }}
            className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
            data-testid="download-manual-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" /> Procédure FR/TH
          </Button>
          <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173D2F]" data-testid="new-refill-tour-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle tournée
          </Button>
        </div>
      </div>

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          {tours === null ? (
            <div className="p-8 text-center text-[#575A57]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</div>
          ) : tours.length === 0 ? (
            <div className="p-12 text-center text-[#575A57]">
              <Truck className="w-12 h-12 mx-auto text-[#A3A6A3] mb-3" />
              <p className="font-medium">Aucune tournée pour le moment.</p>
              <p className="text-sm mt-1">Cliquez sur « Nouvelle tournée » pour commencer.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F0] text-[#575A57]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Référence</th>
                  <th className="text-left px-4 py-3 font-semibold">Statut</th>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Opérateur</th>
                  <th className="text-right px-4 py-3 font-semibold">Distrib.</th>
                  <th className="text-right px-4 py-3 font-semibold">Planifié</th>
                  <th className="text-right px-4 py-3 font-semibold">Chargé</th>
                </tr>
              </thead>
              <tbody>
                {tours.map((t) => {
                  const meta = STATUS_META[t.status] || STATUS_META.BROUILLON;
                  return (
                    <tr
                      key={t.id}
                      onClick={() => navigate(`/tournees-reappro/${t.id}`)}
                      className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7] cursor-pointer"
                      data-testid={`refill-tour-row-${t.reference}`}
                    >
                      <td className="px-4 py-3 font-mono text-[#1A1C1A]">{t.reference}</td>
                      <td className="px-4 py-3"><Badge className={meta.cls}>{meta.label}</Badge></td>
                      <td className="px-4 py-3 text-[#575A57]">{new Date(t.created_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}</td>
                      <td className="px-4 py-3 text-[#575A57]">{t.created_by_name || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono">{t.distributors_count}</td>
                      <td className="px-4 py-3 text-right font-mono">{Number(t.total_planned).toFixed(0)}</td>
                      <td className="px-4 py-3 text-right font-mono">{Number(t.total_loaded).toFixed(0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Dialog : sélection des distributeurs */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle tournée — sélection des distributeurs</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between border-b border-[#E5E5E0] pb-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all-distribs"
                  checked={selectedCount > 0 && selectedCount === distributors.length}
                  onCheckedChange={toggleAll}
                  data-testid="select-all-distribs"
                />
                <Label htmlFor="select-all-distribs" className="font-medium cursor-pointer">
                  Tout sélectionner ({distributors.length})
                </Label>
              </div>
              <span className="text-sm text-[#575A57]">{selectedCount} sélectionné{selectedCount > 1 ? "s" : ""}</span>
            </div>
            {distributors.length === 0 ? (
              <p className="text-sm text-[#575A57] text-center py-4">Aucun distributeur automatique configuré.</p>
            ) : (
              distributors.map((d) => (
                <div key={d.id} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-[#FAFAF7]">
                  <Checkbox
                    id={`distrib-${d.id}`}
                    checked={!!selectedIds[d.id]}
                    onCheckedChange={(c) => setSelectedIds((p) => ({ ...p, [d.id]: !!c }))}
                    data-testid={`distrib-check-${d.code || d.id}`}
                  />
                  <Label htmlFor={`distrib-${d.id}`} className="flex-1 cursor-pointer flex items-center justify-between">
                    <span className="font-medium">{d.name}</span>
                    {(d.city || d.code) && (
                      <span className="text-xs text-[#A3A6A3] flex items-center gap-1">
                        {d.code && <span className="font-mono">{d.code}</span>}
                        {d.city && <><MapPin className="w-3 h-3" />{d.city}</>}
                      </span>
                    )}
                  </Label>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button
              onClick={submitCreate}
              disabled={creating || selectedCount === 0}
              className="bg-[#1F4E3D] hover:bg-[#173D2F]"
              data-testid="submit-create-refill-tour"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer la tournée ({selectedCount})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
